/** Signed 64-bit bounds — the range redis' own integer commands operate in. */
const INT64_MAX = 9223372036854775807n;
const INT64_MIN = -9223372036854775808n;

/**
 * In-memory stand-in for the object node-redis v4's `createClient()` returns,
 * scoped to exactly the command surface `RedisClientWrapper` calls (see
 * RedisClientWrapper.ts). This is NOT a general redis emulator — it exists so
 * unit tests can exercise the redis code path without a real daemon, with
 * enough encoder fidelity to catch the class of bug where an `undefined`
 * field value silently reaches (and corrupts) a real RESP2 connection.
 *
 * COMMAND NAMES ARE PART OF THE SURFACE. node-redis v4 attaches each command
 * under its UPPERCASE name and its camelCase alias — `SADD`/`sAdd`,
 * `SMEMBERS`/`sMembers`, `RPUSH`/`rPush`, `LPOP`/`lPop`, `SCARD`/`sCard` — and
 * NOT under an all-lowercase `sadd`/`smembers`/`rpush`/`lpop`. This fake used
 * to expose the all-lowercase spellings because it was written against
 * `RedisClientWrapper`'s call sites rather than against the real client, so it
 * happily answered six calls a real daemon rejects with `TypeError: ... is not
 * a function` and reported the whole set/list surface green. The names below
 * are the REAL client's; the wrapper was corrected to call them. (Both the
 * spelling and its first reachable caller landed inside the optional-redis
 * effort — 00b43656 and 652a2dc7 — so this was caught in flight, not shipped.)
 *
 * Fidelity notes:
 * - `coerce` mirrors `@redis/client`'s RESP2 argument encoder: strings pass
 *   through, numbers are String-coerced, Buffers are stringified, and
 *   anything else (undefined, null, objects, booleans) throws
 *   `TypeError('Invalid argument type')` — synchronously, before any store
 *   mutation happens, so a rejected write never partially applies.
 * - TTLs are absolute-ms expiries checked lazily via `isExpired` on every
 *   read/write touching a key, reading the clock through `Date.now()` so
 *   vitest fake timers control it in tests.
 * - `incr` parses its stored value exactly as redis does (see that method).
 *
 * KNOWN NON-GOAL, stated so it is never mistaken for fidelity: this fake does
 * not model `WRONGTYPE`. Real redis refuses a string command against a key
 * holding a hash/set/list (and vice versa); here each type lives in its own Map
 * and the same key name can hold several at once. That is a place the fake is
 * laxer than the daemon — uniformly, in every command, not by accident in one —
 * and it is safe only because the store gives every key shape its own name
 * prefix (`p:` / `areas:` / `hold:` / `conn:` / `seqIds` / `seqCounter` / …), so
 * no key is ever addressed with two different types. The real-redis conformance
 * suite is what covers this class of divergence.
 */
export class FakeRedisClient {
    private readonly strings: Map<string, string> = new Map();
    private readonly hashes: Map<string, Map<string, string>> = new Map();
    private readonly sets: Map<string, Set<string>> = new Map();
    private readonly lists: Map<string, string[]> = new Map();
    private readonly expiries: Map<string, number> = new Map();

    /**
     * THE STRICT POSITIONS. node-redis converts numbers to strings in only some
     * argument slots, and everything else reaches the RESP2 encoder raw and
     * throws `TypeError: Invalid argument type`. Verified against the installed
     * @redis/client + redis 8.2.1:
     *
     *   converted: HSET's field AND value (`convertValue`), SET's value
     *   NOT converted: every KEY; HGET/HDEL/HMGET's FIELD; SADD/SREM/RPUSH's
     *                  members (`pushVerdictArguments` concatenates raw)
     *
     * Modelled exactly rather than smoothed over, because the fake being MORE
     * permissive than the daemon is how this effort shipped a store that threw
     * on every numeric-publicId lookup while every fake-backed suite was green.
     * A fake that is permissive where redis is strict does not test the code —
     * it tests the fake.
     */
    private coerceStrict(value: unknown): string {
        if (typeof value === 'string') {
            return value;
        }
        if (Buffer.isBuffer(value)) {
            return value.toString();
        }
        throw new TypeError('Invalid argument type');
    }

    private coerce(value: unknown): string {
        if (typeof value === 'string') {
            return value;
        }
        if (typeof value === 'number') {
            return String(value);
        }
        if (Buffer.isBuffer(value)) {
            return value.toString();
        }
        throw new TypeError('Invalid argument type');
    }

    /** Deletes an expired key from every store and reports whether it was expired. */
    private isExpired(key: string): boolean {
        const expiresAt = this.expiries.get(key);
        if (expiresAt === undefined) {
            return false;
        }
        if (Date.now() < expiresAt) {
            return false;
        }
        this.strings.delete(key);
        this.hashes.delete(key);
        this.sets.delete(key);
        this.lists.delete(key);
        this.expiries.delete(key);
        return true;
    }

    private exists(key: string): boolean {
        return (
            this.strings.has(key) ||
            this.hashes.has(key) ||
            this.sets.has(key) ||
            this.lists.has(key)
        );
    }

    async connect(): Promise<void> {
        // no-op: there is no real connection to establish
    }

    on(_event: string, _listener: (...args: unknown[]) => void): void {
        // no-op: the fake never emits connection lifecycle events
    }

    off(_event: string, _listener: (...args: unknown[]) => void): void {
        // no-op
    }

    // ---- strings ----

    /**
     * `options.EX` is node-redis' one-command "set with expiry" (`SET key value
     * EX n`). Modelled here because a bare `SET` CLEARS any existing TTL — so
     * the expiry has to travel with the write, and a caller that writes then
     * expires is exposed to dying in the gap. Seconds, like the real command.
     */
    async set(key: string, value: unknown, options?: { EX?: number }): Promise<string> {
        key = this.coerceStrict(key);
        this.isExpired(key);
        const coerced = this.coerce(value);
        this.strings.set(key, coerced);
        this.expiries.delete(key);
        if (options?.EX !== undefined) {
            this.expiries.set(key, Date.now() + options.EX * 1000);
        }
        return 'OK';
    }

    async get(key: string): Promise<string | null> {
        key = this.coerceStrict(key);
        this.isExpired(key);
        const value = this.strings.get(key);
        return value === undefined ? null : value;
    }

    async del(key: string): Promise<number> {
        key = this.coerceStrict(key);
        this.isExpired(key);
        const inStrings = this.strings.delete(key);
        const inHashes = this.hashes.delete(key);
        const inSets = this.sets.delete(key);
        const inLists = this.lists.delete(key);
        this.expiries.delete(key);
        return inStrings || inHashes || inSets || inLists ? 1 : 0;
    }

    // ---- hashes ----

    async HSET(
        key: string,
        keyOrValue: string | number | Record<string, unknown> | Map<string, unknown>,
        value?: unknown
    ): Promise<number> {
        key = this.coerceStrict(key);
        this.isExpired(key);

        const pairs: Array<[string, unknown]> = [];
        if (keyOrValue instanceof Map) {
            for (const [field, val] of keyOrValue) {
                pairs.push([String(field), val]);
            }
        } else if (Array.isArray(keyOrValue)) {
            const flat = keyOrValue as unknown[];
            for (let i = 0; i < flat.length; i += 2) {
                pairs.push([String(flat[i]), flat[i + 1]]);
            }
        } else if (typeof keyOrValue === 'object' && keyOrValue !== null) {
            for (const field of Object.keys(keyOrValue)) {
                pairs.push([field, (keyOrValue as Record<string, unknown>)[field]]);
            }
        } else {
            pairs.push([String(keyOrValue), value]);
        }

        // Coerce (and possibly throw) BEFORE mutating any store, so a rejected
        // HSET — e.g. one undefined field among several — never partially applies.
        const coercedPairs: Array<[string, string]> = pairs.map(([field, val]) => [
            field,
            this.coerce(val),
        ]);

        let hash = this.hashes.get(key);
        if (!hash) {
            hash = new Map();
            this.hashes.set(key, hash);
        }

        let added = 0;
        for (const [field, coerced] of coercedPairs) {
            if (!hash.has(field)) {
                added += 1;
            }
            hash.set(field, coerced);
        }
        return added;
    }

    async HGET(key: string, field: string | number): Promise<string | null> {
        key = this.coerceStrict(key);
        // STRICT: HGET's transform is `['HGET', key, field]` — no conversion.
        const wanted = this.coerceStrict(field);
        this.isExpired(key);
        const hash = this.hashes.get(key);
        if (!hash) {
            return null;
        }
        const value = hash.get(wanted);
        return value === undefined ? null : value;
    }

    async HGETALL(key: string): Promise<Record<string, string>> {
        key = this.coerceStrict(key);
        this.isExpired(key);
        const result: Record<string, string> = {};
        const hash = this.hashes.get(key);
        if (!hash) {
            return result;
        }
        for (const [field, value] of hash) {
            result[field] = value;
        }
        return result;
    }

    async HMGET(key: string, fields: Array<string | number>): Promise<Array<string | null>> {
        key = this.coerceStrict(key);
        // STRICT, same as HGET — the fields are not converted by the transform.
        const wanted = fields.map((field) => this.coerceStrict(field));
        this.isExpired(key);
        const hash = this.hashes.get(key);
        return wanted.map((field) => {
            const value = hash?.get(field);
            return value === undefined ? null : value;
        });
    }

    async HDEL(key: string, fields: Array<string | number> | string | number): Promise<number> {
        key = this.coerceStrict(key);
        // STRICT, same as HGET/HMGET.
        const list = (Array.isArray(fields) ? fields : [fields]).map((field) => this.coerceStrict(field));
        this.isExpired(key);
        const hash = this.hashes.get(key);
        if (!hash) {
            return 0;
        }
        let removed = 0;
        for (const field of list) {
            if (hash.delete(field)) {
                removed += 1;
            }
        }
        if (hash.size === 0) {
            this.hashes.delete(key);
        }
        return removed;
    }

    // ---- sets ----

    async sAdd(key: string, members: Array<string | number> | string | number): Promise<number> {
        key = this.coerceStrict(key);
        this.isExpired(key);
        const list = Array.isArray(members) ? members : [members];
        const coerced = list.map((member) => this.coerceStrict(member));

        let set = this.sets.get(key);
        if (!set) {
            set = new Set();
            this.sets.set(key, set);
        }

        let added = 0;
        for (const member of coerced) {
            if (!set.has(member)) {
                set.add(member);
                added += 1;
            }
        }
        return added;
    }

    async sRem(key: string, members: Array<string | number> | string | number): Promise<number> {
        key = this.coerceStrict(key);
        this.isExpired(key);
        const set = this.sets.get(key);
        if (!set) {
            return 0;
        }
        const list = Array.isArray(members) ? members : [members];
        let removed = 0;
        for (const member of list) {
            if (set.delete(this.coerceStrict(member))) {
                removed += 1;
            }
        }
        if (set.size === 0) {
            this.sets.delete(key);
        }
        return removed;
    }

    async sMembers(key: string): Promise<string[]> {
        key = this.coerceStrict(key);
        this.isExpired(key);
        const set = this.sets.get(key);
        return set ? Array.from(set) : [];
    }

    async sCard(key: string): Promise<number> {
        key = this.coerceStrict(key);
        this.isExpired(key);
        const set = this.sets.get(key);
        return set ? set.size : 0;
    }

    // ---- lists ----

    async rPush(key: string, values: Array<string | number> | string | number): Promise<number> {
        key = this.coerceStrict(key);
        this.isExpired(key);
        const list = Array.isArray(values) ? values : [values];
        const coerced = list.map((value) => this.coerceStrict(value));

        let existing = this.lists.get(key);
        if (!existing) {
            existing = [];
            this.lists.set(key, existing);
        }
        existing.push(...coerced);
        return existing.length;
    }

    async lPop(key: string): Promise<string | null> {
        key = this.coerceStrict(key);
        this.isExpired(key);
        const list = this.lists.get(key);
        if (!list || list.length === 0) {
            return null;
        }
        const value = list.shift() as string;
        if (list.length === 0) {
            this.lists.delete(key);
        }
        return value;
    }

    // ---- counters ----

    /**
     * `INCR` returns the NEW value and creates the key at 0 first, so the first
     * call yields 1. Single-command, which is the whole reason the sequential
     * publicId MINT can use it: two gates incrementing concurrently can never
     * be handed the same number.
     *
     * REJECTS EXACTLY WHAT THE DAEMON REJECTS. `INCR` is not "coerce with
     * `Number()` and add one" — redis parses the stored value with a strict
     * signed-64-bit reader and refuses anything else. Probed against redis
     * 8.2.1 (every one of these is an error reply, not a value):
     *
     *   "1.5" "abc" "" " 1" "1 " "+1" "007" "1e3" "-0"
     *          -> ERR value is not an integer or out of range
     *   "9223372036854775808" (does not fit int64) -> same error
     *   "9223372036854775807" (int64 MAX)
     *          -> ERR increment or decrement would overflow   [a DIFFERENT message]
     *   "0" -> 1   "-5" -> -4   "9223372036854775806" -> int64 max   missing key -> 1
     *
     * A permissive `Number()` would have turned `"1.5"` into 2.5 and silently
     * wrapped past int64 — i.e. the fake answering where the daemon errors,
     * which is the exact blind spot that let this effort ship a store throwing
     * on every numeric publicId while every fake-backed suite was green. The
     * counter is INCR-only today so nothing reaches these branches, but a fake
     * that is laxer than redis stops being evidence the moment something does.
     */
    async incr(key: string): Promise<number> {
        key = this.coerceStrict(key);
        this.isExpired(key);
        const current = this.strings.get(key);
        let value = 0n;
        if (current !== undefined) {
            const parsed = FakeRedisClient.parseInt64Strict(current);
            if (parsed === null) {
                throw new Error('ERR value is not an integer or out of range');
            }
            value = parsed;
        }
        if (value === INT64_MAX) {
            // Redis distinguishes this case from an unparseable value; so does this.
            throw new Error('ERR increment or decrement would overflow');
        }
        const next = value + 1n;
        this.strings.set(key, String(next));
        // A RESP2 integer reply, which node-redis hands back as a JS number —
        // lossy above 2^53 in BOTH, so this matches rather than improves on it.
        return Number(next);
    }

    /**
     * Redis' `string2ll`, not `Number()`: optional `-`, then either a single
     * `0` or a leading 1-9 followed by digits, and the result must fit a signed
     * 64-bit integer. No whitespace, no `+`, no leading zeros, no exponent, no
     * fractional part — see `incr` for the probed evidence. `null` means "not an
     * integer", which the caller reports as redis' own error.
     */
    private static parseInt64Strict(raw: string): bigint | null {
        if (!/^-?(0|[1-9][0-9]*)$/.test(raw)) {
            return null;
        }
        // "-0" passes the shape test above (`-` then `0`) but redis rejects it.
        if (raw === '-0') {
            return null;
        }
        const value = BigInt(raw);
        if (value < INT64_MIN || value > INT64_MAX) {
            return null;
        }
        return value;
    }

    // ---- ttl ----

    /** `seconds` matches the real EXPIRE command's unit (seconds, not ms). */
    async expire(key: string, seconds: number): Promise<boolean> {
        key = this.coerceStrict(key);
        if (this.isExpired(key)) {
            return false;
        }
        if (!this.exists(key)) {
            return false;
        }
        this.expiries.set(key, Date.now() + seconds * 1000);
        return true;
    }
}
